import * as React from "react";
import { cn } from "../../lib/utils";

export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /** Opt-in sticky header: caps the wrapper height and pins the header
   *  cells while the body scrolls. Off by default — existing tables keep
   *  their page-scroll behavior. */
  stickyHeader?: boolean;
}

export const Table = ({ className, stickyHeader, ...props }: TableProps) => {
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  // Mobile scroll cue — edge fades shown only while the table actually
  // overflows its wrapper (narrow viewports). Purely cosmetic: tables that
  // fit render exactly the markup they did before, plus an outer wrapper.
  const [cue, setCue] = React.useState({ left: false, right: false });

  const updateCue = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const left = max > 1 && el.scrollLeft > 1;
    const right = max > 1 && el.scrollLeft < max - 1;
    setCue((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  React.useEffect(() => {
    updateCue();
    const el = scrollRef.current;
    // jsdom has no ResizeObserver; the cue simply stays off there.
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(updateCue);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [updateCue]);

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        onScroll={updateCue}
        className={cn(
          "w-full overflow-x-auto rounded-lg border border-border-light shadow-sm bg-white-pure",
          stickyHeader && "max-h-[70vh] overflow-y-auto"
        )}
      >
        <table
          className={cn(
            "w-full border-collapse text-left text-small",
            stickyHeader && "[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-onyx",
            className
          )}
          {...props}
        />
      </div>
      {cue.left && (
        <div
          aria-hidden
          data-testid="table-scroll-cue-left"
          className="pointer-events-none absolute inset-y-0 left-0 w-space-5 rounded-l-lg bg-gradient-to-r from-onyx/15 to-transparent"
        />
      )}
      {cue.right && (
        <div
          aria-hidden
          data-testid="table-scroll-cue-right"
          className="pointer-events-none absolute inset-y-0 right-0 w-space-5 rounded-r-lg bg-gradient-to-l from-onyx/15 to-transparent"
        />
      )}
    </div>
  );
};

export const THead = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <thead className={cn("bg-onyx", className)} {...props} />
);

export const TBody = ({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
  <tbody
    className={cn(
      "[&>tr:nth-child(odd)]:bg-white-pure",
      "[&>tr:nth-child(even)]:bg-white-warm",
      "[&>tr:hover]:bg-gold-highlight/10",
      className
    )}
    {...props}
  />
);

export const TR = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        "transition-colors duration-fast ease-standard border-t border-border-light",
        className
      )}
      {...props}
    />
  )
);
TR.displayName = "TR";

export const TH = ({ className, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
  <th
    scope="col"
    className={cn(
      "px-space-3 py-space-3",
      "font-display text-[10px] font-semibold uppercase tracking-label text-gold",
      className
    )}
    {...props}
  />
);

export const TD = ({ className, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
  <td
    className={cn(
      "px-space-3 py-space-3 text-onyx align-middle",
      className
    )}
    {...props}
  />
);
